import {
  ChargeToRoomTransactionPayload,
  CreateTransactionPayload,
  DeleteTransactionPayload,
  IResDeleteTransactionPayload,
  UpdateTransactionPayload,
} from '@modules/point-of-sale/interface/table-details.interface';

import { frontOfficeApi } from '@api/front-office/front-office.api.rtk';
import { tableGroupAPI } from '@api/pos/pos-bo-table-management.api.rtk';
import { IBaseApiRes } from '@interfaces/generic.interface';
import {
  IPosOrder,
  IPosOrSi,
  IPosReprint,
  IPosReprintOrSiPayload,
  IPosReprintOSPayload,
  IResChargeToRoomPayload,
  IResGenerateOrderSlip,
  IResPrintBill,
  IResReprintOrSiPayload,
  IResReprintOSPayload,
  IVoidOrderSlip,
  TGenerateOrderSlip,
  TPosBillOut,
  TPosDiscountRegular,
  TPosPrintBill,
  TPostOrderPOS,
  TPosVoidDiscount,
  TPosVoidInvoice,
  TPosVoidOrders,
  TPutOrderPOS,
} from '@modules/point-of-sale/interface/orders/pos-orders.interface';
import {
  setIsRefetchOrders,
  setNoRemoveNewItems,
  setTransactionOrders,
  stopIsLoadingTransferTable,
  updateTransactionOrder,
} from '@modules/point-of-sale/slice/pos-table-area.slice';
import { GridRowId } from '@mui/x-data-grid';
import { api } from '@services/api';
import { RootState } from '@store/index';
import { handleError } from '@utils/api-error-handling';
import { encryptPayload } from '@utils/security';

const tagTypes = [
  'Transactions',
  'Transaction',
  'Orders',
  'Order',
  'Order Slips',
  'OR SI',
];
export const posTransactionAPI = api
  .enhanceEndpoints({
    addTagTypes: tagTypes,
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // #region TRANSACTIONS
      getTableInfo: builder.query({
        query: (transactionID: string) =>
          `transactions/${transactionID}/table-info`,
        providesTags: ['Transaction'],
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: (response: IBaseApiRes<any>) => response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      getTransactions: builder.query({
        query: () => 'transactions',
        providesTags: ['Transactions'],
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: (response: IBaseApiRes<any>) => response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      getTransactionsByID: builder.query({
        query: (transactionID: string) => `transactions/${transactionID}`,
        providesTags: ['Transaction'],
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: (response: IBaseApiRes<any>) => response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      createTransaction: builder.mutation({
        query: ({
          payload,
        }: {
          payload: CreateTransactionPayload;
          hasAccountID?: boolean;
        }) => ({
          url: 'transactions',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(posTransactionAPI.util.invalidateTags(['Transactions']));
              dispatch(tableGroupAPI.util.invalidateTags(['TableAreaList']));
              if (arg?.hasAccountID) {
                dispatch(
                  frontOfficeApi.util.invalidateTags(['Account Masterlist']),
                );
              }
            }
          } catch (error) {}
        },
        transformResponse: (response: { data: { transactionID: string } }) =>
          response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      updateTransaction: builder.mutation({
        query: ({
          refId,
          payload,
        }: {
          refId: string;
          payload: UpdateTransactionPayload;
          hasAccountID?: boolean;
        }) => ({
          url: `transactions/${refId}`,
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(tableGroupAPI.util.invalidateTags(['TableDetails']));
            dispatch(tableGroupAPI.util.invalidateTags(['TableAreaList']));
            if (arg?.hasAccountID) {
              dispatch(
                frontOfficeApi.util.invalidateTags(['Account Masterlist']),
              );
            }
          }
        },
        transformResponse: (response: { data: string }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      deleteTransaction: builder.mutation({
        query: ({
          refId,
          payload,
        }: {
          refId: string | GridRowId;
          payload: DeleteTransactionPayload;
        }) => ({
          url: `transactions/${refId}`,
          method: 'DELETE',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(posTransactionAPI.util.invalidateTags(['OR SI']));
          }
        },
        transformResponse: (
          response: IBaseApiRes<IResDeleteTransactionPayload>,
        ) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      postChargeToRoom: builder.mutation({
        query: ({
          payload,
          transactionID,
          printerRefID,
        }: {
          payload: ChargeToRoomTransactionPayload;
          transactionID: string;
          printerRefID: string;
        }) => ({
          url: `transactions/${transactionID}/charge-to-room?printerRefID=${printerRefID}`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (error) {}
        },
        transformResponse: (response: IBaseApiRes<IResChargeToRoomPayload>) =>
          response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      // #endregion

      // #region ORDERS
      getOrders: builder.query({
        query: (transactionID: string) =>
          `transactions/${transactionID}/orders`,
        providesTags: ['Orders'],
        async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
          try {
            const { data } = await queryFulfilled;

            const state = getState() as RootState;
            const transactionDetails = state.posTableArea.transactionDetails;
            const isRefetch = state.posTableArea.status.isRefetchOrders;

            // Prevent applying orders to wrong table
            if (arg !== transactionDetails?.transaction_id) return;

            dispatch(setTransactionOrders({ orders: data, isRefetch }));
            dispatch(setIsRefetchOrders(false));
            dispatch(setNoRemoveNewItems(false));
          } catch (err) {
          } finally {
            dispatch(stopIsLoadingTransferTable());
          }
        },
        transformResponse: (response: IBaseApiRes<{ results: IPosOrder[] }>) =>
          response?.data?.results,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      getOrdersByID: builder.query({
        query: ({
          transactionID,
          orderID,
        }: {
          transactionID: string;
          orderID: string;
        }) => `transactions/${transactionID}/orders/${orderID}`,
        providesTags: ['Order'],
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: (response: IBaseApiRes<any>) => response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      postOrder: builder.mutation({
        query: ({
          transactionID,
          payload,
        }: {
          transactionID: string;
          payload: TPostOrderPOS;
          noRefetch?: boolean;
        }) => ({
          url: `transactions/${transactionID}/orders`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;

            if (data && !arg.noRefetch) {
              dispatch(setIsRefetchOrders(true));
              dispatch(posTransactionAPI.util.invalidateTags(['Orders']));
              dispatch(tableGroupAPI.util.invalidateTags(['TableDetails']));
            }
          } catch (error) {}
        },
        transformResponse: (response: {
          data: { results: { menuID: string; orderID: string }[] };
        }) => response.data.results,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      putOrder: builder.mutation({
        query: ({
          ids: { orderID, transactionID },
          payload,
        }: {
          ids: { transactionID: string; orderID: string };
          payload: TPutOrderPOS;
        }) => ({
          url: `transactions/${transactionID}/orders/${orderID}`,
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (!data) return; // No response

            const propertyWrapper = arg?.payload?.properties?.[0]?.property;
            if (!propertyWrapper) return; // Missing property wrapper

            const posOrders = propertyWrapper?.posOrders;
            if (!Array.isArray(posOrders) || posOrders.length === 0) return; // No orders in payload

            const posOrder = posOrders[0]?.posOrder;
            if (!posOrder) return; // No order data

            const { orderQuantity } = posOrder;
            if (orderQuantity === undefined || orderQuantity === null) return; // Missing quantity

            const orderID = arg?.ids?.orderID;
            if (!orderID) return; // Missing target order ID

            dispatch(
              updateTransactionOrder({
                orderID,
                changes: {
                  quantity: parseFloat(orderQuantity),
                },
              }),
            );
            dispatch(tableGroupAPI.util.invalidateTags(['TableDetails']));
            dispatch(posTransactionAPI.util.invalidateTags(['Orders']));
          } catch (error) {
            console.error('onQueryStarted failed:', error);
          }
        },
        transformResponse: (response: { data: string }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      updatePrintOS: builder.mutation({
        query: ({ osNO }: { osNO: number }) => ({
          url: `transactions/order-slips/${osNO}`,
          method: 'PATCH',
        }),
        async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
          try {
            await queryFulfilled;
            dispatch(setIsRefetchOrders(true));
            dispatch(posTransactionAPI.util.invalidateTags(['Orders']));
          } catch (error) {}
        },
        transformResponse: (response: { data: string }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      postBillOut: builder.mutation({
        query: ({
          transactionID,
          payload,
          printerRefID,
        }: {
          transactionID: string;
          payload: TPosBillOut;
          printerRefID: string;
        }) => ({
          url: `transactions/${transactionID}/invoice?printerRefID=${printerRefID}`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(setIsRefetchOrders(true));
              dispatch(
                posTransactionAPI.util.invalidateTags([
                  'Orders',
                  'Transactions',
                  'Transaction',
                ]),
              );
              dispatch(tableGroupAPI.util.invalidateTags(['TableAreaList']));
            }
          } catch (error) {}
        },
        transformResponse: (response: {
          data: {
            parsedResponse: {
              change: number;
              invoiceNo: number;
              responseCode: number;
              responseMessage: string;
            };
            printData: {
              printer: string;
              printerIPAddress: string;
              payload: [];
            };
          };
        }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),

      deleteVoidInvoice: builder.mutation({
        query: ({
          transactionID,
          payload,
        }: {
          transactionID: string;
          payload: TPosVoidInvoice;
        }) => ({
          url: `transactions/${transactionID}`,
          method: 'DELETE',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(setNoRemoveNewItems(true));
              dispatch(setIsRefetchOrders(true));
              dispatch(
                posTransactionAPI.util.invalidateTags([
                  'Orders',
                  'Transaction',
                ]),
              );
            }
          } catch (error) {}
        },
        transformResponse: (response: { data: string }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      deleteVoidOrders: builder.mutation({
        query: ({
          transactionID,
          payload,
        }: {
          transactionID: string;
          payload: TPosVoidOrders;
        }) => ({
          url: `transactions/${transactionID}/orders`,
          method: 'DELETE',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(setNoRemoveNewItems(true));
              dispatch(setIsRefetchOrders(true));
              dispatch(
                posTransactionAPI.util.invalidateTags([
                  'Orders',
                  'Transaction',
                ]),
              );
              dispatch(tableGroupAPI.util.invalidateTags(['TableDetails']));
            }
          } catch (error) {}
        },
        transformResponse: (response: IBaseApiRes<IVoidOrderSlip>) => {
          return response.data;
        },
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),

      postDiscount: builder.mutation({
        query: ({
          transactionID,
          payload,
        }: {
          transactionID: string;
          payload: TPosDiscountRegular;
        }) => ({
          url: `transactions/${transactionID}/discount`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(
                posTransactionAPI.util.invalidateTags([
                  'Orders',
                  'Transaction',
                ]),
              );
              dispatch(tableGroupAPI.util.invalidateTags(['TableDetails']));
            }
          } catch (error) {}
        },
        transformResponse: (response: { data: string }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      deleteDiscount: builder.mutation({
        query: ({
          transactionID,
          payload,
        }: {
          transactionID: string;
          payload: TPosVoidDiscount;
        }) => ({
          url: `transactions/${transactionID}/discounts`,
          method: 'DELETE',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(
                posTransactionAPI.util.invalidateTags([
                  'Orders',
                  'Transaction',
                ]),
              );
              dispatch(tableGroupAPI.util.invalidateTags(['TableDetails']));
            }
          } catch (error) {}
        },
        transformResponse: (response: {
          data: {
            results: {
              responseCode: number;
              responseMessage: string;
            };
          };
        }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      generateOrderSlip: builder.mutation({
        query: ({
          transactionID,
          payload,
        }: {
          transactionID: string;
          payload: TGenerateOrderSlip;
        }) => ({
          url: `transactions/${transactionID}/order-slip`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
          try {
            await queryFulfilled;
            dispatch(posTransactionAPI.util.invalidateTags(['Orders']));
          } catch (error) {}
        },
        transformResponse: (response: IBaseApiRes<IResGenerateOrderSlip>) =>
          response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      postPrintBill: builder.mutation({
        query: ({
          transactionID,
          payload,
          printerRefID,
        }: {
          transactionID: string;
          payload: TPosPrintBill;
          printerRefID: string;
        }) => ({
          url: `transactions/${transactionID}/billing-statement?printerRefID=${printerRefID}`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
          try {
            await queryFulfilled;
            dispatch(posTransactionAPI.util.invalidateTags(['Orders']));
          } catch (error) {}
        },
        transformResponse: (response: IBaseApiRes<IResPrintBill>) =>
          response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      // #endregion

      // #region REPRINT
      getOrderSlips: builder.query({
        query: (orderSlipID?: string) =>
          `transactions/order-slips${orderSlipID && `/${orderSlipID}`}`,
        providesTags: ['Order Slips'],
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: (
          response: IBaseApiRes<{ results: IPosReprint[] }>,
        ) => response?.data?.results,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),

      getORandSi: builder.query({
        query: ({
          transactionID,
          search,
          isVoided,
        }: {
          transactionID?: string;
          search?: string;
          isVoided?: string;
        }) =>
          `transactions/invoices${transactionID ? `/${transactionID}` : ''}?filter[search]=${search}&filter[isVoided]=${isVoided}`,
        providesTags: ['OR SI'],
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: (response: IBaseApiRes<{ results: IPosOrSi[] }>) =>
          response?.data?.results,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),

      postReprintOrSi: builder.mutation({
        query: ({
          transactionID,
          payload,
        }: {
          transactionID: string | number;
          payload: IPosReprintOrSiPayload;
        }) => ({
          url: `transactions/${transactionID}/invoice/reprint`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (error) {}
        },
        transformResponse: (response: IBaseApiRes<IResReprintOrSiPayload>) =>
          response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),

      postReprintOS: builder.mutation({
        query: ({ payload }: { payload: IPosReprintOSPayload }) => ({
          url: `transactions/order-slip/reprint`,
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (error) {}
        },
        transformResponse: (response: IBaseApiRes<IResReprintOSPayload>) =>
          response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),

      // #endregion
    }),
  });

// #region Export Hooks
export const {
  useCreateTransactionMutation,
  useUpdateTransactionMutation,
  useGetOrdersQuery: useGetOrders,
  usePostOrderMutation: usePostOrder,
  usePutOrderMutation: usePutOrder,
  useDeleteVoidOrdersMutation,
  usePostBillOutMutation: usePostPOSBillOut,
  usePostDiscountMutation: usePostDiscount,
  useGenerateOrderSlipMutation: useGenerateOrderSlip,
  usePostPrintBillMutation: usePostPrintBill,
  useGetOrderSlipsQuery,
  useGetORandSiQuery,
  usePostReprintOrSiMutation: usePostReprintOrSi,
  usePostReprintOSMutation: usePostReprintOS,
  useUpdatePrintOSMutation: useUpdatePrintOS,
  useDeleteTransactionMutation: useDeleteTransaction,
  usePostChargeToRoomMutation: usePostChargeToRoom,
  useDeleteDiscountMutation: useDeleteDiscount,
} = posTransactionAPI;
// #endregion Export Hooks
